import { forwardRef, ButtonHTMLAttributes, ReactNode } from 'react'
import { cn } from 'utils/cn'

type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'danger' | 'danger-soft' | 'ghost' | 'outline'
type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant
    size?: ButtonSize
    loading?: boolean
    icon?: ReactNode
    iconRight?: ReactNode
    fullWidth?: boolean
}

const variantClasses: Record<ButtonVariant, string> = {
    primary:
        'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 focus-visible:ring-blue-600 dark:bg-blue-500 dark:hover:bg-blue-600 dark:active:bg-blue-700',
    secondary:
        'border border-slate-300 bg-white text-slate-900 hover:bg-slate-50 active:bg-slate-100 focus-visible:ring-blue-600 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800 dark:active:bg-slate-700',
    tertiary:
        'bg-slate-100 text-slate-900 hover:bg-slate-200 active:bg-slate-300 focus-visible:ring-blue-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 dark:active:bg-slate-600',
    danger: 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800 focus-visible:ring-red-600 dark:bg-red-500 dark:hover:bg-red-600 dark:active:bg-red-700',
    'danger-soft':
        'bg-red-50 text-red-700 hover:bg-red-100 active:bg-red-200 focus-visible:ring-red-600 dark:bg-red-950 dark:text-red-400 dark:hover:bg-red-900 dark:active:bg-red-800',
    ghost: 'bg-transparent text-slate-600 hover:bg-slate-100 active:bg-slate-200 focus-visible:ring-blue-600 dark:text-slate-400 dark:hover:bg-slate-800 dark:active:bg-slate-700',
    outline:
        'border border-slate-300 bg-transparent text-slate-900 hover:bg-slate-50 active:bg-slate-100 focus-visible:ring-blue-600 dark:border-slate-600 dark:text-slate-100 dark:hover:bg-slate-800 dark:active:bg-slate-700',
}

// Sizing follows the compact, SaaS-dashboard conventions used by Linear /
// Vercel / Stripe: smaller heights, tighter padding, `text-sm` (14px) at
// the default `md` size instead of `text-base`. The pre-redesign values
// (h-8 / h-10 / h-12 with `rounded-3xl`) read as a consumer app — too tall
// and too pill-shaped. Current tokens keep buttons visually calm on dense
// data views (tables, settings drawers) without shrinking below the 32px
// minimum-click-target guideline for primary actions.
const sizeClasses: Record<ButtonSize, string> = {
    sm: 'px-2.5 py-1 text-xs gap-1.5 h-7',
    md: 'px-3 py-1.5 text-sm gap-1.5 h-8',
    lg: 'px-4 py-2 text-sm gap-2 h-10',
}

// Spinner scales to the button's text size via `em`-based sizing so the
// indicator never looks oversized on the compact `sm` button or undersized
// on `lg`. `em` honours the `text-xs/sm` token set on the parent.
const Spinner = () => (
    <svg className="h-[1em] w-[1em] animate-spin" viewBox="0 0 24 24" fill="none">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
)

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    ({ variant = 'primary', size = 'md', loading, icon, iconRight, fullWidth, className = '', children, disabled, ...props }, ref) => {
        return (
            <button
                ref={ref}
                disabled={disabled || loading}
                // Numeo DS default component radius: `rounded-lg` (8px). Matches
                // SendButton, SegmentedControl track, StatCard, Dropdown — every
                // primary interactive surface. `font-medium` (500) keeps the subtle
                // weight used by Radix / shadcn / Vercel Geist.
                className={cn(
                    'inline-flex cursor-pointer items-center justify-center rounded-lg font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed',
                    variantClasses[variant],
                    sizeClasses[size],
                    fullWidth && 'w-full',
                    className
                )}
                {...props}
            >
                {loading ? <Spinner /> : icon}
                {children}
                {iconRight}
            </button>
        )
    }
)

Button.displayName = 'Button'
